import { useId } from "react"

import { Form, Input } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { getInvalidCodeAccessValues } from "@/pages/DeploymentsPage/application/useAgentPlatform.selector"
import {
  CloudConnectionFields,
  CloudConnectionToggle
} from "@/pages/DeploymentsPage/components/molecules/CloudConnectionStatus"

import { getFieldStatus } from "./helpers"

import type { AgentCoreCodeAccessFieldsProps } from "."

const CODE_ACCESS_FIELDS = ["buckets", "kmsKeyArns"] as const

// The buckets of the runtimes deployed from a code upload, asked for only
// when there are such runtimes. They're a parameter of the stack alone, which
// its command sets: Kodem keeps no copy of them.
export const AgentCoreCodeAccessFields = ({
  isUpdate,
  value,
  onChange
}: AgentCoreCodeAccessFieldsProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.agentCore.cloud"
  })
  const id = useId()
  const { codeUpload, access } = value

  return (
    <>
      <CloudConnectionToggle
        checked={codeUpload}
        onChange={(checked) => onChange({ ...value, codeUpload: checked })}
        label={t("codeAccess.title")}
        help={t(
          isUpdate ? "codeAccess.descriptionUpdate" : "codeAccess.description"
        )}
      />
      {codeUpload && (
        <CloudConnectionFields>
          {CODE_ACCESS_FIELDS.map((field) => {
            const invalid = getInvalidCodeAccessValues(field, access[field])

            return (
              <Form.Item
                key={field}
                label={t(`codeAccess.${field}.label`)}
                help={
                  invalid.length
                    ? t(`codeAccess.${field}.error`, {
                        values: invalid.join(", ")
                      })
                    : t(`codeAccess.${field}.help`)
                }
                validateStatus={getFieldStatus(
                  !!access[field].trim(),
                  !invalid.length
                )}
                hasFeedback
                htmlFor={`${id}-${field}`}
              >
                <Input
                  id={`${id}-${field}`}
                  value={access[field]}
                  placeholder={t(`codeAccess.${field}.placeholder`)}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      access: { ...access, [field]: e.target.value }
                    })
                  }
                />
              </Form.Item>
            )
          })}
        </CloudConnectionFields>
      )}
    </>
  )
}
